import React, { useState, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';

import { Form } from '@unform/web';
import { isEmpty } from 'lodash';
import moment from 'moment';
import PropTypes from 'prop-types';
import * as Yup from 'yup';

import { Spinner } from '~/components';
import { Input, DateTime, Autocomplete, Combobox } from '~/components/Form';
import {
  MOTORISTA_ABASTECIMENTO,
  LOCALIDADE_ABASTECIMENTO,
  MODULO_20_GRAUS,
} from '~/constants/modules';
import { can, canURL, t, validCharacters } from '~/helpers';
import api from '~/services/api';
import {
  updateOperacaoTransferencia,
  storeOperacaoTransferencia,
  update,
} from '~/store/modules/operacaotransferencia/actions';

export default function CreateOrUpdate({
  reference,
  data,
  create,
  toggleLoading,
  toggleConfirmButton,
}) {
  const dispatch = useDispatch();

  const [posto, setPosto] = useState(0);
  const [localidadeFixa, setLocalidadeFixa] = useState(null);
  const [submittedData, setSubmittedData] = useState({});
  const [motorista, setMotorista] = useState(null);
  const [controla20graus, setControla20graus] = useState(0);
  const [postoDestino, setPostoDestino] = useState(0);
  const { internounidatausuario, formaidfrentistaabast } = useSelector(
    (state) => state.user.configs
  );
  const [exibemotoristaterminal, setExibemotoristaterminal] = useState(0);
  const [
    exibemotoristatransferenciaterminal,
    setExibemotoristatransferenciaterminal,
  ] = useState(0);
  const [pendingRequests, setPendingRequests] = useState(0);
  const [hasLoadingError, setHasLoadingError] = useState(false);
  const loadingErrorMessage = 's.geral.loadingerror';

  const [
    diasliberadigitacaomovimentacaobico,
    setDiasliberadigitacaomovimentacaobico,
  ] = useState(1);

  const { client } = useSelector((state) => state.user.profile);
  const { loading, find, params } = useSelector(
    (state) => state.operacaotransferencia
  );

  const initials = create ? data : { ...find, ...data };
  const formInitials = create ? { ...initials, ...submittedData } : initials;
  const { pathname } = useLocation();

  const isDisabled = useMemo(
    () => !create && pathname === '/operacaotransferencia',
    [create, pathname]
  );
  const rotaPermissao =
    pathname === '/operacaoedicaotransferencia'
      ? '/operacaotransferencia'
      : pathname;
  const permiteEditarEncerrantes =
    !create && canURL(rotaPermissao).altera_encerrante;

  const handleAsyncLoadingChange = React.useCallback(
    (isLoading) => {
      setPendingRequests((state) => {
        if (hasLoadingError) return 0;

        if (isLoading) return state + 1;

        return state > 0 ? state - 1 : 0;
      });
    },
    [hasLoadingError]
  );

  const handleAsyncLoadingError = React.useCallback(() => {
    setHasLoadingError(true);
    setPendingRequests(0);
  }, []);

  const handleTrackApiRequest = React.useCallback(
    async (request) => {
      handleAsyncLoadingChange(true);

      try {
        return await request();
      } catch (error) {
        handleAsyncLoadingError();
        throw error;
      } finally {
        handleAsyncLoadingChange(false);
      }
    },
    [handleAsyncLoadingChange, handleAsyncLoadingError]
  );

  async function handleValidateMotorista() {
    handleAsyncLoadingChange(true);

    await api
      .get(`motoristas/${motorista}`, { params: { pesquisalista: true } })
      .catch(async () => {
        (await reference.current) !== null &&
          reference.current.setFieldValue('idmotorista', null);

        reference.current !== null &&
          reference.current.setFieldError(
            'idmotorista',
            'Motorista não encontrado!'
          );
      })
      .finally(() => {
        handleAsyncLoadingChange(false);
      });
  }

  async function handleSubmit(param) {
    try {
      if (hasLoadingError) {
        toast.error(t(loadingErrorMessage));
        return;
      }

      if (pendingRequests > 0) {
        toast.warning(t('s.geral.validacao.aguardecarregamento'));
        return;
      }

      if (create) {
        setSubmittedData(param);
      }

      const schema = Yup.object().shape({
        idposto: Yup.string()
          .required(t('s.geral.validacao.campoobrigatorio'))
          .typeError(t('s.geral.validacao.campoobrigatorio')),
        idbico: Yup.string()
          .required(t('s.geral.validacao.campoobrigatorio'))
          .typeError(t('s.geral.validacao.campoobrigatorio')),
        datahorainicialmovimentacao: create
          ? Yup.string()
              .test(
                'campo-existe',
                t('s.geral.validacao.campoobrigatorio').replace(
                  '%s',
                  'datahorainicialmovimentacao'
                ),
                function (value) {
                  const data = this.parent;

                  if (!('datahorainicialmovimentacao' in data)) {
                    const msg = `(Data hora inicial) - ${t(
                      's.geral.validacao.campoobrigatorio'
                    ).replace('%s', 'datahorainicialmovimentacao')}`;
                    toast.error(msg);
                    return this.createError({ message: msg });
                  }

                  return true;
                }
              )
              .required(t('s.geral.validacao.campoobrigatorio'))
              .test(
                'min-date',
                t('s.geral.validacao.data_minima').replace(
                  '%s',
                  diasliberadigitacaomovimentacaobico
                ),
                (value) => {
                  if (!value) return true; // já tratado pelo required
                  const date = moment(value, 'DD/MM/YYYY HH:mm:ss');
                  return (
                    date.isValid() &&
                    date.isSameOrAfter(
                      moment().subtract(
                        diasliberadigitacaomovimentacaobico,
                        'days'
                      )
                    )
                  );
                }
              )
              .test('max-date', t('s.geral.validacao.data_maxima'), (value) => {
                if (!value) return true;
                const date = moment(value, 'DD/MM/YYYY HH:mm:ss');
                return date.isValid() && date.isSameOrBefore(moment());
              })
          : Yup.string()
              .required(t('s.geral.validacao.campoobrigatorio'))
              .typeError(t('s.geral.validacao.campoobrigatorio')),
        datahorafinalmovimentacao: create
          ? Yup.string()
              .test(
                'campo-existe',
                t('s.geral.validacao.campoobrigatorio').replace(
                  '%s',
                  'datahorafinalmovimentacao'
                ),
                function (value) {
                  const data = this.parent;

                  if (!('datahorafinalmovimentacao' in data)) {
                    const msg = `(Data hora final) - ${t(
                      's.geral.validacao.campoobrigatorio'
                    ).replace('%s', 'datahorafinalmovimentacao')}`;
                    toast.error(msg);
                    return this.createError({ message: msg });
                  }

                  return true;
                }
              )
              .required(t('s.geral.validacao.campoobrigatorio'))
              .test(
                'after-inicial',
                t('s.geral.validacao.data_final_maior_inicial'),
                function (value) {
                  const { datahorainicialmovimentacao } = this.parent;
                  if (!value || !datahorainicialmovimentacao) return true;
                  const start = moment(
                    datahorainicialmovimentacao,
                    'DD/MM/YYYY HH:mm:ss'
                  );
                  const end = moment(value, 'DD/MM/YYYY HH:mm:ss');
                  return end.isValid() && end.isSameOrAfter(start);
                }
              )
              .test(
                'not-future',
                t('s.geral.validacao.data_final_maior_hoje'),
                (value) => {
                  if (!value) return true;
                  const end = moment(value, 'DD/MM/YYYY HH:mm:ss');
                  return end.isValid() && end.isSameOrBefore(moment()); // não permite data futura
                }
              )
          : Yup.string()
              .required(t('s.geral.validacao.campoobrigatorio'))
              .typeError(t('s.geral.validacao.campoobrigatorio')),
        // datahorainicialmovimentacao: Yup.string()
        //   .required(t('s.geral.validacao.campoobrigatorio'))
        //   .typeError(t('s.geral.validacao.campoobrigatorio')),
        // datahorafinalmovimentacao: Yup.string()
        //   .required(t('s.geral.validacao.campoobrigatorio'))
        //   .typeError(t('s.geral.validacao.campoobrigatorio')),
        idpostodestinomovimentacaotransferencia: Yup.string()
          .required(t('s.geral.validacao.campoobrigatorio'))
          .typeError(t('s.geral.validacao.campoobrigatorio')),
        encerrantemovimentacaotransferencia: Yup.number()
          .required(t('s.geral.validacao.campoobrigatorio'))
          .typeError(t('s.geral.validacao.campoobrigatorio')),
        encerranteinicialmovimentacao: Yup.number()
          .required(t('s.geral.validacao.campoobrigatorio'))
          .typeError(t('s.geral.validacao.campoobrigatorio')),
        encerrantefinalmovimentacao: Yup.number()
          .required(t('s.geral.validacao.campoobrigatorio'))
          .typeError(t('s.geral.validacao.campoobrigatorio')),
        volumemovimentacao: Yup.number()
          .required(t('s.geral.validacao.campoobrigatorio'))
          .test(
            'encerrantes-volume',
            t('s.geral.validacao.valorvolumediferencaencerrantes'),
            function validateEncerrantesVolume(value) {
              const {
                encerranteinicialmovimentacao,
                encerrantefinalmovimentacao,
              } = this.parent;

              if (
                value === undefined ||
                encerranteinicialmovimentacao === undefined ||
                encerrantefinalmovimentacao === undefined
              ) {
                return true;
              }

              return (
                Math.abs(
                  Number(value) -
                    (Number(encerrantefinalmovimentacao) -
                      Number(encerranteinicialmovimentacao))
                ) <= 0.1
              );
            }
          ),
        codigousuario:
          formaidfrentistaabast !== 0
            ? Yup.string()
                .test(
                  'characters-invalid',
                  t('s.configuracao.geral.naopodecontercaracterespecial'),
                  (value) => validCharacters(value)
                )
                .required(t('s.geral.validacao.campoobrigatorio'))
            : Yup.string(),
        idmotorista:
          can(MOTORISTA_ABASTECIMENTO) &&
          exibemotoristatransferenciaterminal > 0
            ? Yup.string()
                .required(t('s.geral.validacao.campoobrigatorio'))
                .typeError(t('s.geral.validacao.campoobrigatorio'))
            : Yup.string(),

        idlocalidade: can(LOCALIDADE_ABASTECIMENTO)
          ? Yup.number()
              .required(t('s.geral.validacao.campoobrigatorio'))
              .typeError(t('s.geral.validacao.campoobrigatorio'))
          : Yup.string(),
      });

      await schema.validate(param, {
        abortEarly: false,
      });

      const objSend = {
        volumepredeterminadomovimentacao: 0.0,
        modomovimentacao: 0,
        formaidmovimentacao: 0,
        numerologmovimentacao: 0,
        onlinemovimentacao: 0,
        vazaomediamovimentacao: 0,
        vazaomaximamovimentacao: 0,

        codigousuario: '',

        ...initials,
        ...param,
        idterminal: isEmpty(param.idterminal)
          ? initials.idterminal
          : param.idterminal,
        idproduto: isEmpty(param.idproduto)
          ? initials.idproduto
          : param.idproduto,
        idcliente: client,
      };

      if (create) {
        setSubmittedData(objSend);
      }

      if (create) {
        dispatch(storeOperacaoTransferencia(objSend, params));
      } else if (pathname === '/operacaotransferencia') {
        dispatch(updateOperacaoTransferencia(objSend, params));
      } else {
        dispatch(update(objSend, params));
      }
    } catch (err) {
      const validationErrors = {};

      if (err instanceof Yup.ValidationError) {
        err.inner.forEach((error) => {
          validationErrors[error.path] = error.message.replace(
            `${error.path} `,
            ''
          );
        });

        reference.current.setErrors(validationErrors);
      }
    }
  }

  const handleChangepostoscomboios = React.useCallback((value) => {
    if (value.idposto) {
      setPosto(value.idposto);
      setLocalidadeFixa(
        Number(value.idlocalidade) > 0 ? Number(value.idlocalidade) : null
      );
      setControla20graus(value.controla20grausposto);
      // setOperabastecimento20posto(value.operabastecimento20posto);
    } else {
      setLocalidadeFixa(null);
      setControla20graus(0);
      // setOperabastecimento20posto(0);
      // novo
      setPosto(null);
    }
    // setDiasliberadigitacaomovimentacaobico(-2)
  }, []);

  React.useEffect(() => {
    toggleLoading(pendingRequests > 0);
  }, [pendingRequests, toggleLoading]);

  React.useEffect(() => {
    toggleConfirmButton(!hasLoadingError);
  }, [hasLoadingError, toggleConfirmButton]);

  React.useEffect(
    () => () => {
      toggleLoading(false);
      toggleConfirmButton(true);
    },
    [toggleLoading, toggleConfirmButton]
  );

  return (
    <>
      {loading ? (
        <div style={{ height: '14rem', width: '50rem' }}>
          <Spinner loading />
        </div>
      ) : hasLoadingError ? (
        <div
          style={{
            border: '1px solid #f5c6cb',
            background: '#f8d7da',
            color: '#721c24',
            borderRadius: '4px',
            padding: '16px',
            lineHeight: 1.5,
          }}
        >
          {t(loadingErrorMessage)}
        </div>
      ) : (
        <div style={{ position: 'relative', minHeight: '14rem' }}>
          <Form
            ref={reference}
            initialData={formInitials}
            onSubmit={handleSubmit}
          >
            {/* <h1>{JSON.stringify(diasliberadigitacaomovimentacaobico)}</h1> */}
            <Autocomplete
              disabled={!create}
              name="idposto"
              label="codigonomeposto"
              url="postoscomboios"
              unique="idposto"
              title={t('s.geral.pontoorigem')}
              getValue={(value) => {
                handleChangepostoscomboios(value);
                // if (value?.idposto) {
                //   setPosto(value.idposto);
                //   setControla20graus(value.controla20grausposto);
                // } else {
                //   setPosto(0);
                //   setControla20graus(0);
                // }
                // setDiasliberadigitacaomovimentacaobico(0)
              }}
              filterOptions={(options, { inputValue }) =>
                options.filter(
                  (opt) =>
                    opt.idposto !== postoDestino &&
                    (opt.label
                      ?.toLowerCase()
                      .includes(inputValue.toLowerCase()) ||
                      opt.nomeposto
                        ?.toLowerCase()
                        .includes(inputValue.toLowerCase()))
                )
              }
              onLoadingChange={handleAsyncLoadingChange}
              onLoadingError={handleAsyncLoadingError}
            />

            {create ? (
              posto ? (
                <Combobox
                  disabled={isDisabled}
                  name="idbico"
                  label="bicoprodutobico"
                  url="bicos"
                  unique="idbico"
                  title={t('s.menu.configuracao.bico')}
                  hiddens={[{ name: 'idterminal' }, { name: 'idproduto' }]}
                  params={{
                    wherefield: !create
                      ? 'idposto'
                      : ['idposto', 'bloqueiadigitacaomovimentacaobico'],
                    valuewherefield: !create
                      ? posto || initials.idposto
                      : [posto || initials.idposto, 'S'],
                    typewherefield: !create ? 'EQUALS' : ['EQUALS', 'EQUALS'],
                    fieldlist:
                      'idbico, idterminal, idproduto, bicoprodutobico, tipoproduto, exibemotoristaterminal, exibemotoristatransferenciaterminal, diasliberadigitacaomovimentacaobico',
                  }}
                  getValue={(value) => {
                    if (value?.exibemotoristaterminal) {
                      setExibemotoristaterminal(value?.exibemotoristaterminal);
                      setExibemotoristatransferenciaterminal(
                        value?.exibemotoristatransferenciaterminal
                      );
                    } else {
                      setExibemotoristaterminal(0);
                      setExibemotoristatransferenciaterminal(0);
                    }
                    if (value?.diasliberadigitacaomovimentacaobico) {
                      setDiasliberadigitacaomovimentacaobico(
                        value?.diasliberadigitacaomovimentacaobico
                      );
                    } else {
                      setDiasliberadigitacaomovimentacaobico(1);
                    }
                  }}
                  onLoadingChange={handleAsyncLoadingChange}
                  onLoadingError={handleAsyncLoadingError}
                />
              ) : (
                <></>
              )
            ) : (
              <Combobox
                disabled={isDisabled}
                name="idbico"
                label="bicoprodutobico"
                url="bicos"
                unique="idbico"
                title={t('s.menu.configuracao.bico')}
                hiddens={[{ name: 'idterminal' }, { name: 'idproduto' }]}
                params={{
                  wherefield: !create
                    ? 'idposto'
                    : ['idposto', 'bloqueiadigitacaomovimentacaobico'],
                  valuewherefield: !create
                    ? posto || initials.idposto
                    : [posto || initials.idposto, 'S'],
                  typewherefield: !create ? 'EQUALS' : ['EQUALS', 'EQUALS'],
                  fieldlist:
                    'idbico, idterminal, idproduto, bicoprodutobico, tipoproduto, exibemotoristaterminal, exibemotoristatransferenciaterminal, diasliberadigitacaomovimentacaobico',
                }}
                getValue={(value) => {
                  if (value?.exibemotoristaterminal) {
                    setExibemotoristaterminal(value?.exibemotoristaterminal);
                    setExibemotoristatransferenciaterminal(
                      value?.exibemotoristatransferenciaterminal
                    );
                  } else {
                    setExibemotoristaterminal(0);
                    setExibemotoristatransferenciaterminal(0);
                  }
                  if (value?.diasliberadigitacaomovimentacaobico) {
                    setDiasliberadigitacaomovimentacaobico(
                      value?.diasliberadigitacaomovimentacaobico
                    );
                  } else {
                    setDiasliberadigitacaomovimentacaobico(1);
                  }
                }}
                onLoadingChange={handleAsyncLoadingChange}
                onLoadingError={handleAsyncLoadingError}
              />
            )}

            {create ? (
              posto && Number(diasliberadigitacaomovimentacaobico) > 0 ? (
                <>
                  <DateTime
                    time
                    name="datahorainicialmovimentacao"
                    disabled={isDisabled}
                    label={t(
                      's.operacao.abastecimento.datahorainicialmovimentacao'
                    )}
                    minDate={moment().subtract(
                      Number(diasliberadigitacaomovimentacaobico),
                      'days'
                    )} // ex: permite no máximo 7 dias atrás
                    maxDate={moment()}
                    shouldDisableDate={(day) =>
                      day.isBefore(
                        moment().subtract(
                          Number(diasliberadigitacaomovimentacaobico),
                          'days'
                        )
                      )
                    }
                    disableMaskedInput
                    helperText=""
                  />

                  <DateTime
                    time
                    name="datahorafinalmovimentacao"
                    disabled={isDisabled}
                    label={t(
                      's.operacao.abastecimento.datahorafinalmovimentacao'
                    )}
                    minDate={moment().subtract(
                      Number(diasliberadigitacaomovimentacaobico),
                      'days'
                    )} // ex: permite no máximo 7 dias atrás
                    maxDate={moment()}
                    shouldDisableDate={(day) =>
                      day.isBefore(
                        moment().subtract(
                          Number(diasliberadigitacaomovimentacaobico),
                          'days'
                        )
                      )
                    }
                    disableMaskedInput
                    helperText=""
                  />
                </>
              ) : (
                <></>
              )
            ) : (
              <>
                <DateTime
                  disabled={isDisabled}
                  time
                  name="datahorainicialmovimentacao"
                  // disabled={!create}
                  label={t(
                    's.operacao.abastecimento.datahorainicialmovimentacao'
                  )}
                />

                <DateTime
                  disabled={isDisabled}
                  time
                  name="datahorafinalmovimentacao"
                  // disabled={!create}
                  label={t(
                    's.operacao.abastecimento.datahorafinalmovimentacao'
                  )}
                />
              </>
            )}

            {/* <DateTime
            disabled={isDisabled}
            time
            name="datahorainicialmovimentacao"
            label={t('s.operacao.abastecimento.datahorainicialmovimentacao')}
          />

          <DateTime
            disabled={isDisabled}
            time
            name="datahorafinalmovimentacao"
            label={t('s.operacao.abastecimento.datahorafinalmovimentacao')}
          /> */}

            <Autocomplete
              id="idpostodestinomovimentacaotransferencia"
              name="idpostodestinomovimentacaotransferencia"
              label="codigonomeposto"
              url="postoscomboios"
              unique="idposto"
              title={t('s.geral.pontodestino')}
              getValue={(value) => {
                if (value?.idposto) {
                  setPostoDestino(value.idposto);
                } else {
                  setPostoDestino(0);
                }
              }}
              filterOptions={(options, { inputValue }) =>
                options.filter(
                  (opt) =>
                    opt.idposto !== posto &&
                    (opt.label
                      ?.toLowerCase()
                      .includes(inputValue.toLowerCase()) ||
                      opt.nomeposto
                        ?.toLowerCase()
                        .includes(inputValue.toLowerCase()))
                )
              }
              onLoadingChange={handleAsyncLoadingChange}
              onLoadingError={handleAsyncLoadingError}
            />

            <Input
              name="encerrantemovimentacaotransferencia"
              type="number"
              label={t(
                's.operacao.transferencia.encerrantemovimentacaotransferencia'
              )}
            />

            {can(LOCALIDADE_ABASTECIMENTO) && (
              <Autocomplete
                name="idlocalidade"
                label="nomelocalidade"
                url="localidades"
                unique="idlocalidade"
                title="Localidade"
                disabled={create && Number(localidadeFixa) > 0}
                forcedValue={
                  create && Number(localidadeFixa) > 0
                    ? localidadeFixa
                    : undefined
                }
                onLoadingChange={handleAsyncLoadingChange}
                onLoadingError={handleAsyncLoadingError}
              />
            )}

            <Input
              disabled={!create && !permiteEditarEncerrantes}
              name="encerranteinicialmovimentacao"
              label={t(
                's.operacao.abastecimento.encerranteinicialmovimentacao'
              )}
              type="number"
            />

            <Input
              disabled={!create && !permiteEditarEncerrantes}
              name="encerrantefinalmovimentacao"
              label={t('s.operacao.abastecimento.encerrantefinalmovimentacao')}
              type="number"
            />

            <Input
              disabled={!create}
              name="volumemovimentacao"
              label={t('s.operacao.abastecimento.volumemovimentacao')}
              type="number"
            />

            {can(MOTORISTA_ABASTECIMENTO) &&
              exibemotoristatransferenciaterminal > 0 && (
                <Input
                  name="idmotorista"
                  label={t('s.operacao.abastecimento.idmotorista')}
                  value={motorista}
                  getValue={(value) => setMotorista(value)}
                  onBlur={() => handleValidateMotorista()}
                  onKeyUp={({ key }) =>
                    key === 'Enter' && handleValidateMotorista()
                  }
                />
              )}

            {formaidfrentistaabast !== 0 && (
              <Input
                name="codigousuario"
                label={t('s.operacao.abastecimento.codigousuario')}
              />
            )}

            {can(MODULO_20_GRAUS) && controla20graus === 1 && !create && (
              <>
                <Input
                  name="temperaturamediamovimentacao"
                  type="number"
                  disabled
                  label={t('s.operacao.abastecimento.temperaturamedia')}
                />
                <Input
                  name="densidade20movimentacao"
                  type="number"
                  disabled
                  label={t('s.operacao.abastecimento.densidade20')}
                />
                <Input
                  name="volume20movimentacao"
                  type="number"
                  disabled
                  label={t('s.operacao.abastecimento.volume20movimentacao')}
                />
              </>
            )}
          </Form>
          <Spinner loading={pendingRequests > 0} />
        </div>
      )}
    </>
  );
}

CreateOrUpdate.propTypes = {
  reference: PropTypes.objectOf(PropTypes.any).isRequired,
  create: PropTypes.bool.isRequired,
  data: PropTypes.objectOf(PropTypes.any),
  toggleLoading: PropTypes.func,
  toggleConfirmButton: PropTypes.func,
};

CreateOrUpdate.defaultProps = {
  data: {},
  toggleLoading: () => {},
  toggleConfirmButton: () => {},
};
